import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import config from "../../config.js";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-bs5';
import 'datatables.net-select-bs5';
import 'datatables.net-responsive-bs5';
// import 'datatables.net-dt/css/dataTables.dataTables.css';
// import 'datatables.net-select-dt/css/select.dataTables.css';

import "datatables.net-buttons";
import "datatables.net-buttons/js/buttons.print.js";
import "datatables.net-buttons/js/buttons.html5.js";
import "datatables.net-buttons/js/buttons.colVis.js";
import "datatables.net-buttons/js/dataTables.buttons.js";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as JSZip from "jszip";

/*
 * REACT DATATABLE CONVERSION NOTES:
 * This file has been converted from jQuery DataTable to React DataTable (datatables.net-react)
 * 
 * Key Changes:
 * 1. Replaced $.DataTable initialization with React DataTable component
 * 2. Moved jQuery-specific logic to React hooks and useMemo
 * 3. Updated imports to use React DataTable packages
 * 4. Preserved all original functionality including buttons, filters, and custom configurations
 * 
 * The component maintains full compatibility with the existing API and props.
 */

DataTable.use(DT);

// Safely attach virtual fonts to pdfMake across different bundlers/module shapes
if (pdfFonts && typeof pdfFonts === 'object') {
    if (pdfFonts.vfs) {
        pdfMake.vfs = pdfFonts.vfs;
    } else if (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
    } else {
        console.warn('pdfmake vfs not found on vfs_fonts module. The PDF export button may fail. Module:', pdfFonts);
    }
} else {
    console.warn('vfs_fonts module did not load as an object:', pdfFonts);
}
window.JSZip = JSZip;

const CustomDataTable = ({
    onFileSelect,
    columns,
    url,
    table_id,
    columnDefs = [],
    order = [[0, "desc"]],
    FilterParams = []
}) => {
    const tableRef = useRef(null);

    // Helper to safely get nested value
    const getNestedValue = useCallback((row, keyPath) => {
        try {
            return keyPath.split('.').reduce((acc, key) => acc?.[key], row) ?? '';
        } catch (e) {
            return '';
        }
    }, []);

    // Helper function to log current DataTable states
    const logDataTableStatus = useCallback((tableId, context = '', additionalInfo = {}) => {
        console.log(`[React DataTable Status ${context}] Table ID: ${tableId}`, {
            tableExists: tableRef.current !== null,
            timestamp: new Date().toISOString(),
            userToken: localStorage.getItem('token') ? 'Present' : 'Missing',
            language: localStorage.getItem('lang') || 'en',
            ...additionalInfo
        });
    }, []);

    // React DataTable options configuration
    const dataTableOptions = useMemo(() => {
        const transfiles = { en: "/DataTables/English.json", ar: '/DataTables/Arabic.json' };

        console.log('[CustomDataTable DEBUG] Initializing DataTable:', {
            table_id: table_id,
            url: config.SystemUrl + url,
            columns: columns,
            FilterParams: FilterParams,
            columnDefs: columnDefs,
            order: order
        });

        return {
            responsive: true,
            searchDelay: 50,
            processing: true,
            serverSide: true,
            select: {
                style: 'multi'
            },
            scrollX: false,
            autoWidth: true,
            ajax: {
                url: config.SystemUrl + url,
                data: function (data) {
                    console.log('[CustomDataTable DEBUG] Initial request data:', data);
                    console.log('[CustomDataTable DEBUG] Request URL:', config.SystemUrl + url);
                    console.log('[CustomDataTable DEBUG] Table ID:', table_id);
                    console.log('[CustomDataTable DEBUG] FilterParams:', FilterParams);

                    FilterParams.forEach(el => {
                        // Use modern DOM queries instead of jQuery
                        const selectElement = document.querySelector(`select[name="data[${el}]"]`);
                        const inputElement = document.querySelector(`input[name="data[${el}]"]`);
                        const selectVal = selectElement ? selectElement.value : null;
                        const inputVal = inputElement ? inputElement.value : null;
                        const value = selectVal || inputVal;

                        // Convert undefined/null to empty string to prevent "undefined" in URLs
                        const cleanValue = (value === undefined || value === null || value === 'undefined') ? '' : value;

                        if (cleanValue !== '') {
                            data[el] = cleanValue;
                        }

                        console.log(`[CustomDataTable DEBUG] Filter ${el}:`, {
                            selectVal: selectVal,
                            inputVal: inputVal,
                            rawValue: value,
                            cleanValue: cleanValue,
                            elementExists: selectElement || inputElement
                        });
                    });

                    console.log('[CustomDataTable DEBUG] Final request data:', data);
                    return data;
                },
                dataSrc: function (json) {
                    console.log('[CustomDataTable DEBUG] Response received:', json);
                    console.log('[CustomDataTable DEBUG] Response data array:', json.data);
                    console.log('[CustomDataTable DEBUG] Response recordsTotal:', json.recordsTotal);
                    console.log('[CustomDataTable DEBUG] Response recordsFiltered:', json.recordsFiltered);

                    if (!json.data) {
                        console.warn('[CustomDataTable DEBUG] WARNING: No data array in response!', {
                            fullResponse: json,
                            keys: Object.keys(json || {}),
                            table_id: table_id
                        });
                        return [];
                    }

                    if (json.data.length === 0) {
                        console.warn('[CustomDataTable DEBUG] WARNING: Empty data array received!', {
                            recordsTotal: json.recordsTotal,
                            recordsFiltered: json.recordsFiltered,
                            table_id: table_id,
                            url: config.SystemUrl + url
                        });
                    } else {
                        console.log('[CustomDataTable DEBUG] SUCCESS: Received', json.data.length, 'records for table:', table_id);
                    }

                    return json.data;
                },
                error: function (xhr, error, code) {
                    console.error('[CustomDataTable DEBUG] AJAX Error:', {
                        xhr: xhr,
                        error: error,
                        code: code,
                        status: xhr.status,
                        statusText: xhr.statusText,
                        responseText: xhr.responseText,
                        tableId: table_id,
                        url: config.SystemUrl + url
                    });

                    try {
                        const errorResponse = JSON.parse(xhr.responseText);
                        console.error('[CustomDataTable DEBUG] Parsed error response:', errorResponse);
                    } catch (e) {
                        console.error('[CustomDataTable DEBUG] Could not parse error response as JSON');
                    }
                },
                xhrFields: {
                    withCredentials: true
                },
                beforeSend: function (xhr) {
                    console.log('[CustomDataTable DEBUG] Sending request to:', config.SystemUrl + url);
                    console.log('[CustomDataTable DEBUG] Table ID:', table_id);
                    try {
                        const t = localStorage.getItem('token');
                        if (t) {
                            xhr.setRequestHeader('Authorization', 'Bearer ' + t);
                            console.log('[CustomDataTable DEBUG] Authorization header set');
                        } else {
                            console.warn('[CustomDataTable DEBUG] No token found in localStorage!');
                        }
                        const lang = localStorage.getItem('lang') || 'en';
                        xhr.setRequestHeader('lang', lang);
                        console.log('[CustomDataTable DEBUG] Language header set:', lang);
                    } catch (e) {
                        console.error('[CustomDataTable DEBUG] Error setting headers:', e);
                    }
                }
            },
            columns: columns.map((col, index) => {
                console.log(`[CustomDataTable DEBUG] Column ${index}:`, {
                    data: col.data,
                    dataType: typeof col.data,
                    title: col.title
                });
                if (col.data && typeof col.data === 'string' && col.data.includes('.')) {
                    return {
                        ...col,
                        render: function (data, type, row) {
                            return getNestedValue(row, col.data);
                        }
                    };
                }
                return col;
            }),
            order: order,
            columnDefs: columnDefs,
            dom: '<"top"<"left-col"B><"center-col"l><"right-col"f>>rtip',
            buttons: [
                'csv',
                'excelHtml5',
                {
                    extend: 'print',
                    exportOptions: { columns: ':visible' }
                },
                {
                    extend: 'copy',
                    exportOptions: { columns: [0, ':visible'] }
                },
                {
                    extend: 'pdf',
                    exportOptions: { columns: [0, 1, 2, 3, 4] }
                },
                {
                    extend: 'colvis',
                    postfixButtons: ['colvisRestore']
                },
            ],
            lengthMenu: [[10, 25, 50, 100, 1000, -1], [10, 25, 50, 100, 1000, "All"]],
            language: {
                url: transfiles[localStorage.getItem('lang') || 'en']
            },
            drawCallback: function (settings, json) {
                console.log('[CustomDataTable DEBUG] Draw callback executed:', {
                    settings: settings,
                    json: json,
                    displayedRows: settings.aiDisplay?.length || 0,
                    totalRows: settings._iDisplayLength
                });

            }
        };
    }, [columns, url, table_id, columnDefs, order, FilterParams, getNestedValue]);

    // Listen for custom reload events
    useEffect(() => {
        const handleReload = (event) => {
            console.log('[CustomDataTable DEBUG] Reload event received:', {
                eventTableId: event.detail.tableId,
                currentTableId: table_id,
                willReload: event.detail.tableId === table_id
            });
            if (event.detail.tableId === table_id && tableRef.current) {
                console.log('[CustomDataTable DEBUG] Reloading table:', table_id);
                tableRef.current.dt().ajax.reload();
            }
        };

        window.addEventListener('datatableReload', handleReload);

        return () => {
            window.removeEventListener('datatableReload', handleReload);
        };
    }, [table_id]);

    useEffect(() => {
        logDataTableStatus(table_id, 'COMPONENT_MOUNTED');
    }, [table_id, logDataTableStatus]);

    return (
        <div className="table-responsive" style={{ overflow: 'auto' }}>
            <DataTable
                ref={tableRef}
                id={table_id}
                className="table table-striped table-hover dt-responsive responsive display"
                options={dataTableOptions}
                style={{ width: '100%' }}
                cellSpacing="0"
            />
        </div>
    );
}
export default CustomDataTable;
